import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Terminal, RotateCcw, ChevronDown, ChevronRight } from 'lucide-react';
import { Role } from '@jeket-cloud/types/auth';
import { clusterApi } from '@/api/client';
import RoleGate from '@/components/auth/RoleGate';
import type { PodDetailContainer } from '@/types';
import { usePodDetail } from './useClusterData';
import PodLogsViewer from './PodLogsViewer';
import PodExecDrawer from './PodExecDrawer';

interface Props {
  siteId: string;
  namespace: string;
  podName: string;
  onClose: () => void;
}

function StatusDot({ state }: { state: string }) {
  const cls = state === 'running' ? 'bg-status-healthy'
    : state === 'waiting' ? 'bg-status-warning'
    : state === 'terminated' ? 'bg-status-error'
    : 'bg-silver-muted';
  return <span className={`w-2 h-2 rounded-full inline-block ${cls}`} />;
}

function ConditionBadge({ status }: { status: string }) {
  return (
    <span className={`text-xs px-1.5 py-0.5 rounded ${status === 'True' ? 'text-status-healthy bg-status-healthy/10' : 'text-status-error bg-status-error/10'}`}>
      {status}
    </span>
  );
}

function ContainerDetail({ container }: { container: PodDetailContainer }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-surface-elevated rounded-lg">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-elevated/30 transition-colors"
      >
        <StatusDot state={container.state} />
        {expanded ? <ChevronDown size={12} className="text-gold" /> : <ChevronRight size={12} className="text-silver-muted" />}
        <span className="text-xs text-silver-bright font-mono flex-1">{container.name}</span>
        <span className="text-xs text-silver-muted">{container.state}</span>
        {container.restartCount > 0 && (
          <span className="text-xs text-status-warning">{container.restartCount} restarts</span>
        )}
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-2 border-t border-surface-elevated">
          <div className="grid grid-cols-2 gap-2 text-xs mt-2">
            <div><span className="text-silver-muted">Image: </span><span className="text-silver-bright font-mono break-all">{container.image}</span></div>
            <div><span className="text-silver-muted">State: </span><span className="text-silver-bright">{container.state}{container.reason ? ` (${container.reason})` : ''}</span></div>
            {container.exitCode !== undefined && (
              <div><span className="text-silver-muted">Exit Code: </span><span className="text-silver-bright font-mono">{container.exitCode}</span></div>
            )}
            {container.lastState && (
              <div><span className="text-silver-muted">Last State: </span><span className="text-silver-bright">{container.lastState.state}{container.lastState.reason ? ` (${container.lastState.reason})` : ''}</span></div>
            )}
          </div>

          {/* Resources */}
          <div className="text-xs">
            <h5 className="text-silver-muted uppercase tracking-wider text-[10px] mb-1">Resources</h5>
            <div className="grid grid-cols-2 gap-1">
              <div>CPU req: <span className="text-silver-bright font-mono">{container.resources.requests.cpu || '—'}</span></div>
              <div>CPU lim: <span className="text-silver-bright font-mono">{container.resources.limits.cpu || '—'}</span></div>
              <div>Mem req: <span className="text-silver-bright font-mono">{container.resources.requests.memory || '—'}</span></div>
              <div>Mem lim: <span className="text-silver-bright font-mono">{container.resources.limits.memory || '—'}</span></div>
            </div>
          </div>

          {/* Mounts */}
          {container.volumeMounts.length > 0 && (
            <div className="text-xs">
              <h5 className="text-silver-muted uppercase tracking-wider text-[10px] mb-1">Volume Mounts</h5>
              {container.volumeMounts.map((vm, i) => (
                <div key={i} className="font-mono text-silver-bright">
                  {vm.name} → {vm.mountPath}{vm.readOnly ? ' (ro)' : ''}
                </div>
              ))}
            </div>
          )}

          {/* Env */}
          {container.env.length > 0 && (
            <div className="text-xs">
              <h5 className="text-silver-muted uppercase tracking-wider text-[10px] mb-1">Environment</h5>
              <div className="max-h-32 overflow-y-auto space-y-0.5">
                {container.env.map((e, i) => (
                  <div key={i} className="font-mono">
                    <span className="text-silver-bright">{e.name}</span>
                    <span className="text-silver-muted">=</span>
                    <span className={e.value.startsWith('<secret:') ? 'text-status-warning' : 'text-silver-bright'}>{e.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function PodDetailDrawer({ siteId, namespace, podName, onClose }: Props) {
  const [showLogs, setShowLogs] = useState(false);
  const [showShell, setShowShell] = useState(false);
  const [confirmRestart, setConfirmRestart] = useState(false);

  const queryClient = useQueryClient();
  const { data: pod, isLoading } = usePodDetail(siteId, namespace, podName);

  const restartMutation = useMutation({
    mutationFn: () => clusterApi.restartPod(siteId, namespace, podName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cluster-pods', siteId] });
      setConfirmRestart(false);
      onClose();
    },
  });

  function openLogs() {
    setShowLogs(true);
  }

  function openShell() {
    setShowShell(true);
  }

  if (showLogs && pod) {
    const containers = pod.containers.map((c) => c.name);
    return (
      <PodLogsViewer
        siteId={siteId}
        namespace={namespace}
        podName={podName}
        containers={containers}
        onClose={() => setShowLogs(false)}
      />
    );
  }

  if (showShell && pod) {
    // v1: shell into the first container only — multi-container picker is
    // out of scope per the issue. Engineers can re-open with a different
    // container by closing + reopening once that UI lands.
    const firstContainer = pod.containers[0]?.name;
    return (
      <PodExecDrawer
        siteId={siteId}
        namespace={namespace}
        podName={podName}
        container={firstContainer}
        onClose={() => setShowShell(false)}
      />
    );
  }

  return (
    <div className="fixed inset-y-0 right-0 z-40 w-[520px] max-w-full bg-surface border-l border-surface-elevated shadow-2xl flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-surface-elevated">
        <div className="min-w-0">
          <h3 className="text-sm font-medium text-silver-bright truncate">{podName}</h3>
          <p className="text-xs text-silver-muted">{namespace}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => openLogs()}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs bg-surface-elevated hover:bg-surface-elevated/80 text-silver-bright rounded-lg transition-colors"
          >
            <Terminal size={12} /> Logs
          </button>
          {/* Engineer-only "Open Shell" — pod exec / web shell (#249). Gated
              by RoleGate so the button is invisible to lower personas; the
              BFF + deployment-service still enforce the gate (defense in
              depth). */}
          <RoleGate role={Role.Engineer}>
            <button
              onClick={() => openShell()}
              data-testid="pod-detail-open-shell"
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs bg-gold/10 hover:bg-gold/20 text-gold rounded-lg transition-colors"
            >
              <Terminal size={12} /> Shell
            </button>
          </RoleGate>
          {!confirmRestart ? (
            <button
              onClick={() => setConfirmRestart(true)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs bg-status-warning/10 hover:bg-status-warning/20 text-status-warning rounded-lg transition-colors"
            >
              <RotateCcw size={12} /> Restart
            </button>
          ) : (
            <div className="flex items-center gap-1">
              <button
                onClick={() => restartMutation.mutate()}
                disabled={restartMutation.isPending}
                className="px-2.5 py-1.5 text-xs bg-status-error/20 hover:bg-status-error/30 text-status-error rounded-lg transition-colors"
              >
                {restartMutation.isPending ? 'Deleting...' : 'Confirm'}
              </button>
              <button
                onClick={() => setConfirmRestart(false)}
                className="px-2 py-1.5 text-xs text-silver-muted hover:text-silver-bright"
              >
                Cancel
              </button>
            </div>
          )}
          <button onClick={onClose} className="text-silver-muted hover:text-silver-bright ml-1">
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {isLoading || !pod ? (
          <div className="space-y-3 animate-pulse">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-16 bg-surface-elevated rounded-lg" />
            ))}
          </div>
        ) : (
          <>
            {/* Status overview */}
            <Section title="Status">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <KV label="Phase" value={pod.phase} />
                <KV label="Node" value={pod.nodeName} mono />
                <KV label="Pod IP" value={pod.ip} mono />
                <KV label="Host IP" value={pod.hostIp} mono />
                <KV label="QoS" value={pod.qosClass} />
                <KV label="Age" value={pod.age} />
                {pod.ownerKind && <KV label="Owner" value={`${pod.ownerKind}/${pod.ownerName}`} mono />}
              </div>
            </Section>

            {/* Conditions */}
            <Section title="Conditions">
              <div className="space-y-1">
                {pod.conditions.map((c, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <ConditionBadge status={c.status} />
                    <span className="text-silver-bright">{c.type}</span>
                    {c.reason && <span className="text-silver-muted">({c.reason})</span>}
                  </div>
                ))}
              </div>
            </Section>

            {/* Containers */}
            <Section title={`Containers (${pod.containers.length})`}>
              <div className="space-y-2">
                {pod.containers.map((c) => (
                  <ContainerDetail key={c.name} container={c} />
                ))}
              </div>
            </Section>

            {/* Init Containers */}
            {pod.initContainers.length > 0 && (
              <Section title={`Init Containers (${pod.initContainers.length})`}>
                <div className="space-y-1">
                  {pod.initContainers.map((c, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <StatusDot state={c.state} />
                      <span className="text-silver-bright font-mono">{c.name}</span>
                      <span className="text-silver-muted">{c.image}</span>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Events */}
            {pod.events.length > 0 && (
              <Section title={`Events (${pod.events.length})`}>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {pod.events.map((e, i) => (
                    <div key={i} className="text-xs border-l-2 pl-2 border-surface-elevated">
                      <div className="flex items-center gap-1.5">
                        <span className={`font-medium ${e.type === 'Warning' ? 'text-status-warning' : 'text-silver-bright'}`}>
                          {e.reason}
                        </span>
                        {e.count > 1 && <span className="text-silver-muted">x{e.count}</span>}
                      </div>
                      <p className="text-silver-muted">{e.message}</p>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Volumes */}
            {pod.volumes.length > 0 && (
              <Section title={`Volumes (${pod.volumes.length})`}>
                <div className="space-y-1">
                  {pod.volumes.map((v, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <span className="text-silver-bright font-mono">{v.name}</span>
                      <span className="px-1.5 py-0.5 bg-surface-elevated rounded text-silver-muted text-[10px]">{v.type}</span>
                      {v.source && <span className="text-silver-muted font-mono">{v.source}</span>}
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Labels */}
            {Object.keys(pod.labels).length > 0 && (
              <Section title="Labels">
                <div className="flex flex-wrap gap-1">
                  {Object.entries(pod.labels).map(([k, v]) => (
                    <span key={k} className="text-[10px] font-mono px-1.5 py-0.5 bg-surface-elevated rounded text-silver-muted">
                      {k}={v}
                    </span>
                  ))}
                </div>
              </Section>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="text-xs font-medium text-silver-muted uppercase tracking-wider mb-2">{title}</h4>
      {children}
    </div>
  );
}

function KV({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <span className="text-silver-muted">{label}: </span>
      <span className={`text-silver-bright ${mono ? 'font-mono' : ''}`}>{value || '—'}</span>
    </div>
  );
}
